import { useState } from "react";
import { Button } from "../../../components/button";
import { useTeam } from "../../../context/TeamContext";
import { useToast } from "../../../components/toast";
import { TeamsAPI } from "../../../APIs/teams.api";
import { TeamWithDetails } from "../../../types/team.types";
import { CircleCheck, CircleX } from "lucide-react";

interface TeamFamilyStatusProps {
  onStatusChange?: (isFamilyTeam: boolean) => void;
  team: TeamWithDetails;
}

export function TeamFamilyStatus({
  onStatusChange,
  team,
}: TeamFamilyStatusProps) {
  const { getTeam } = useTeam();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const isFamilyTeam = team?.categories.some((cat) => cat.category_id === 3);
  const hasEnoughMembers = team?.members && team.members.length === 3;

  const handleToggleFamilyStatus = async () => {
    if (!team || !hasEnoughMembers) return;

    try {
      setIsLoading(true);
      const newStatus = !isFamilyTeam;

      if (team.id) {
        await TeamsAPI.updateTeamFamilyCategory(newStatus);
      }

      await getTeam();

      addToast({
        message: newStatus
          ? "El equipo ahora está marcado como familia."
          : "Se quitó la categoría de equipo familia.",
        type: "success",
        location: "bottom-center",
      });

      if (onStatusChange) {
        onStatusChange(newStatus);
      }
    } catch (error) {
      addToast({
        message: "Hubo un error al actualizar la categoría del equipo.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasEnoughMembers) {
    return null;
  }

  return (
    <div
      className={`mb-4 p-4 ${
        isFamilyTeam
          ? "bg-green-100 border border-green-300"
          : "bg-blue-100 border border-blue-300"
      } rounded-xl`}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h3
            className={`text-lg font-semibold flex items-center gap-2 ${
              isFamilyTeam ? "text-green-800" : "text-blue-800"
            }`}
          >
            {isFamilyTeam ? "Equipo familia ✓" : "Categoría familia"}
          </h3>
          <p
            className={`mt-1 ${
              isFamilyTeam ? "text-green-700" : "text-blue-700"
            }`}
          >
            {isFamilyTeam
              ? "Este equipo está marcado como familia."
              : "Si todos los miembros son familia, podes marcar este equipo como familia."}
          </p>
        </div>

        <div className="flex items-center">
          <Button
            text={
              isLoading
                ? "Procesando..."
                : isFamilyTeam
                ? "Quitar categoría"
                : "Marcar como familia"
            }
            icon={
              isFamilyTeam ? (
                <CircleX className="mr-1" />
              ) : (
                <CircleCheck className="mr-1" />
              )
            }
            variant={isFamilyTeam ? "delete" : "create"}
            className="whitespace-nowrap"
            onClick={handleToggleFamilyStatus}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
